import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  Autocomplete,
  Box,
  IconButton,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { NotificationManager } from "react-notifications";
import {
  SaveOrderSample,
  UpdateOrderSample,
} from "../../services/OrderService";
import CommonLoader from "../common/CommonLoader";
import {
  PRIORITY_OPTIONS,
  LAB_OPTIONS,
  withCurrentValue,
} from "./sampleFieldOptions";
import { EMPTY_LOOKUPS } from "./useSampleLookups";
import QuickAddValueDialog from "./QuickAddValueDialog";

// Use the date/time exactly as the API sends it ("2026-09-25T12:40:00"), cut
// to what a datetime-local input takes ("2026-09-25T12:40").
const formatDateTimeForInput = (dateString) =>
  dateString ? dateString.slice(0, 16) : "";

const AddSampleDialog = ({
  open,
  onClose,
  onAdd,
  sample,
  isEditing,
  lookups = EMPTY_LOOKUPS,
}) => {
  const [formData, setFormData] = useState({
    orderNumber: "",
    sampleNumber: "",
    labNumber: "",
    dateReceived: "",
    originalSampleDate: "",
    samtype: "",
    county: "",
    sampleMatrix: "",
    labId: "",
    dateCollected: "",
    collectedBy: "",
    priority: "",
    processId: "",
    sampleDescription: "",
    sampleIdentity: "",
    comments: "",
    rush: false,
    complianceSample: false,
    facilityId: "",
    samplingPointId: "",
    otherLabName: "",
    // Not shown on the form. samples.SampleType is the IDEM sample
    // classification, which VLP edits on its own control; carry the stored
    // value through so saving here does not clear it.
    sampleType: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Lookup lists behind the dropdowns are loaded once by the samples table and
  // shared with the expanded row panels, so they arrive as a prop.
  const {
    sampleTypes,
    sampleMatrices,
    facilityIds,
    samplingPointIds,
    sampleLocations,
    validationProcesses,
    addFacilityId,
    addSamplingPointId,
  } = lookups;

  // Which client-scoped list the "+" is currently adding to, if any.
  const [quickAdd, setQuickAdd] = useState(null);

  const quickAddConfig = {
    facilityId: {
      title: "Add Facility ID",
      label: "Facility ID",
      onAdd: addFacilityId,
    },
    samplingPointId: {
      title: "Add Sampling Point ID",
      label: "Sampling Point ID",
      onAdd: addSamplingPointId,
    },
  };

  // Adding from here also selects the new value, which is the point of the
  // shortcut - you add it because it is the one you need on this sample.
  const handleQuickAdd = async (name) => {
    const added = await quickAddConfig[quickAdd].onAdd(name);
    if (added) {
      setField(quickAdd, added);
    }
  };

  useEffect(() => {
    if (sample) {
      setFormData({
        orderNumber: sample.orderNumber || "",
        sampleNumber: sample.sampleNumber || "",
        labNumber: sample.labNumber || "",
        dateReceived: formatDateTimeForInput(sample.dateReceived),
        originalSampleDate: sample.originalSampleDate || "",
        samtype: sample.samtype || "",
        county: sample.county || "",
        sampleMatrix: sample.sampleMatrix || "",
        labId: sample.labId || "",
        dateCollected: sample.dateCollected || "",
        collectedBy: sample.collectedBy || "",
        priority: sample.priority || "",
        processId: sample.processId || "",
        sampleDescription: sample.sampleDescription || "",
        sampleIdentity: sample.sampleIdentity || "",
        comments: sample.comments || "",
        rush: sample.rush || false,
        complianceSample: sample.complianceSample || false,
        facilityId: sample.facilityId || "",
        samplingPointId: sample.samplingPointId || "",
        otherLabName: sample.otherLabName || "",
        // ?? rather than || so a stored classification of 0 (Routine) survives.
        sampleType: sample.sampleType ?? "",
      });
    }
  }, [sample]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  // Autocomplete reports its value separately from a change event, so the
  // free-text fields backed by a search list set their field directly.
  const setField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value ?? "" }));
  };

  const handleAddClick = () => {
    if (validateForm()) {
      const data = {
        orderNumber: formData.orderNumber
          ? parseInt(formData.orderNumber, 10)
          : null,
        sampleNumber: formData.sampleNumber
          ? parseInt(formData.sampleNumber, 10)
          : null,
        labNumber: formData.labNumber || "",
        // Sent exactly as entered; VLP reads the time from date_received. The
        // API fills time_received from this value.
        dateReceived: formData.dateReceived || null,
        originalSampleDate: formData.originalSampleDate || null,
        samtype: formData.samtype || "",
        county: formData.county || "",
        sampleMatrix: formData.sampleMatrix || "",
        labId: formData.labId || "",
        dateCollected: formData.dateCollected || null,
        collectedBy: formData.collectedBy || "",
        priority: formData.priority || "",
        processId: formData.processId || "",
        sampleDescription: formData.sampleDescription || "",
        sampleIdentity: formData.sampleIdentity || "",
        comments: formData.comments || "",
        rush:
          formData.rush === true || formData.rush === false
            ? formData.rush
            : null,
        complianceSample:
          formData.complianceSample === true ||
          formData.complianceSample === false
            ? formData.complianceSample
            : null,
        facilityId: formData.facilityId || "",
        samplingPointId: formData.samplingPointId || "",
        otherLabName: formData.otherLabName || "",
        sampleType:
          formData.sampleType === "" || formData.sampleType === null
            ? null
            : parseInt(formData.sampleType, 10),
      };

      handleAddCallBackClick({
        ...data,
        recordid: sample?.recordid || 0,
      });
    }
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    return dateString.split("T")[0] ?? "";
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.sampleNumber) {
      errors.sampleNumber = "Sample Number is required";
    }
    if (!formData.labNumber) {
      errors.labNumber = "Lab Number is required";
    }
    if (!formData.dateReceived) {
      errors.dateReceived = "Date Received is required";
    }
    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleClose = () => {
    setControlsEmpty();
    onClose();
  };

  const setControlsEmpty = () => {
    setErrors({});
    setFormData({
      orderNumber: "",
      sampleNumber: "",
      labNumber: "",
      dateReceived: "",
      originalSampleDate: "",
      samtype: "",
      county: "",
      sampleMatrix: "",
      labId: "",
      dateCollected: "",
      collectedBy: "",
      priority: "",
      processId: "",
      sampleDescription: "",
      sampleIdentity: "",
      comments: "",
      rush: false,
      complianceSample: false,
      facilityId: "",
      samplingPointId: "",
      otherLabName: "",
      sampleType: "",
    });
  };

  const handleAddCallBackClick = async (sample) => {
    try {
      let response;
      setLoading(true);
      if (isEditing) {
        // Update existing formula
        response = await UpdateOrderSample(sample);
      } else {
        // Add new formula
        response = await SaveOrderSample(sample);
      }

      if (response.status === 200) {
        NotificationManager.success(
            isEditing
            ? "Order Sample updated successfully"
            : "Order Sample created successfully"
        );
        handleClose();
        window.location.reload(false);
        setControlsEmpty();
      } else {
        NotificationManager.error(
            isEditing
            ? "Error updating Order Sample"
            : "Error creating Order Sample"
        );
      }
    } catch (error) {
      NotificationManager.error(
        isEditing
          ? "Error updating Order Sample"
          : "Error creating Order Sample"
      );
    } finally {
      setLoading(false);
    }
  };

  // One markup path for every dropdown so they all size and blank the same way.
  // `quickAddKey` opts a field into the "+" button VLP puts next to Facility Id
  // and Sampling Point for adding a value to the client's list on the spot.
  const renderSelect = (label, name, options, quickAddKey) => {
    const select = (
      <TextField
        size="small"
        select
        label={label}
        name={name}
        value={formData[name] ?? ""}
        onChange={handleInputChange}
        fullWidth
      >
        <MenuItem value="">
          <em>None</em>
        </MenuItem>
        {withCurrentValue(options, formData[name]).map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
    );

    if (!quickAddKey) {
      return select;
    }

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>{select}</Box>
        <Tooltip title={`Add ${label}`}>
          <span>
            <IconButton
              size="small"
              color="primary"
              onClick={() => setQuickAdd(quickAddKey)}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  };

  // Type-ahead over a maintenance list that still accepts a value typed by
  // hand, matching how VLP lets you either pick from the lookup window or key
  // the value in.
  const renderSearchable = (label, name, options) => (
    <Autocomplete
      freeSolo
      options={options}
      value={formData[name] ?? ""}
      onChange={(event, newValue) => setField(name, newValue)}
      onInputChange={(event, newInput) => setField(name, newInput)}
      renderInput={(params) => (
        <TextField {...params} label={label} name={name} size="small" fullWidth />
      )}
    />
  );

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="lg">
        <DialogTitle>{isEditing ? "Edit Sample" : "Add New Sample"}</DialogTitle>
        <DialogContent>
          <form>
            {/* 15 columns so each xs={3} cell is a fifth: five fields per row. */}
            <Grid container columns={15} spacing={2} style={{ padding: "20px" }}>
              <Grid item xs={3}>
                <TextField
                  size="small"
                  label="Order Number"
                  name="orderNumber"
                  value={formData.orderNumber}
                  onChange={handleInputChange}
                  fullWidth
                  disabled
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  size="small"
                  label="Sample Number"
                  name="sampleNumber"
                  value={formData.sampleNumber}
                  onChange={handleInputChange}
                  fullWidth
                  error={!!errors.sampleNumber}
                  helperText={errors.sampleNumber}
                />
              </Grid>
              {/* One picker for both parts: VLP keeps the received time
                  inside date_received, so a separate time box would never
                  reach it. */}
              <Grid item xs={3}>
                <TextField
                  size="small"
                  label="Date/Time Received"
                  name="dateReceived"
                  value={formData.dateReceived}
                  onChange={handleInputChange}
                  type="datetime-local"
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  error={!!errors.dateReceived}
                  helperText={errors.dateReceived}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  size="small"
                  label="Original Sample Date"
                  name="originalSampleDate"
                  value={formatDateForInput(formData.originalSampleDate)}
                  onChange={handleInputChange}
                  type="date"
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={3}>
                {renderSelect("Sample Type", "samtype", sampleTypes)}
              </Grid>
              <Grid item xs={3}>
                <TextField
                  size="small"
                  label="County"
                  name="county"
                  value={formData.county}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={3}>
                {renderSelect("Sample Matrix", "sampleMatrix", sampleMatrices)}
              </Grid>
              <Grid item xs={3}>
                <TextField
                  size="small"
                  label="Lab ID"
                  name="labId"
                  value={formData.labId}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  size="small"
                  label="Date Collected"
                  name="dateCollected"
                  value={formatDateForInput(formData.dateCollected)}
                  onChange={handleInputChange}
                  type="date"
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  size="small"
                  label="Collected By"
                  name="collectedBy"
                  value={formData.collectedBy}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={3}>
                {renderSelect(
                  "Priority",
                  "priority",
                  PRIORITY_OPTIONS.map((value) => ({ value, label: value }))
                )}
              </Grid>
              <Grid item xs={3}>
                {renderSearchable("Process ID", "processId", validationProcesses)}
              </Grid>
              <Grid item xs={3}>
                <TextField
                  size="small"
                  label="Sample Description"
                  name="sampleDescription"
                  value={formData.sampleDescription}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={3}>
                {renderSearchable(
                  "Sample Location",
                  "sampleIdentity",
                  sampleLocations
                )}
              </Grid>
              <Grid item xs={3}>
                <TextField
                  size="small"
                  label="Comments"
                  name="comments"
                  value={formData.comments}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      name="rush"
                      checked={formData.rush}
                      onChange={handleInputChange}
                    />
                  }
                  label="Rush"
                />
              </Grid>
              <Grid item xs={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      name="complianceSample"
                      checked={formData.complianceSample}
                      onChange={handleInputChange}
                    />
                  }
                  label="Compliance Sample"
                />
              </Grid>
              <Grid item xs={3}>
                {renderSelect(
                  "Facility ID",
                  "facilityId",
                  facilityIds,
                  "facilityId"
                )}
              </Grid>
              <Grid item xs={3}>
                {renderSelect(
                  "Sampling Point ID",
                  "samplingPointId",
                  samplingPointIds,
                  "samplingPointId"
                )}
              </Grid>
              {/* Lab and Lab Number sit together so the lab and the number it
                  issued read as one pair, as requested in ENB-561. */}
              <Grid item xs={3}>
                {renderSelect("Lab", "otherLabName", LAB_OPTIONS)}
              </Grid>
              <Grid item xs={3}>
                <TextField
                  size="small"
                  label="Lab Number"
                  name="labNumber"
                  value={formData.labNumber}
                  onChange={handleInputChange}
                  fullWidth
                  error={!!errors.labNumber}
                  helperText={errors.labNumber}
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleAddClick} variant="contained" color="primary">
            {sample ? "Save" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
      <QuickAddValueDialog
        open={quickAdd !== null}
        title={quickAdd ? quickAddConfig[quickAdd].title : ""}
        label={quickAdd ? quickAddConfig[quickAdd].label : ""}
        onClose={() => setQuickAdd(null)}
        onAdd={handleQuickAdd}
      />
      {loading && <CommonLoader loading={loading} />}
    </>
  );
};

export default AddSampleDialog;
